import React, { useState } from 'react';
import { 
  Briefcase, 
  Shield, 
  Landmark, 
  HeartPulse, 
  Laptop, 
  Shirt, 
  ExternalLink, 
  Search, 
  Mail, 
  Phone, 
  Crown, 
  Users, 
  Info,
  MessageSquare,
  Globe,
  Instagram
} from 'lucide-react';
import { UserProfile } from '../types';

interface PartnersHubProps {
  userProfile: UserProfile | null;
  isPremiumOrVip: boolean;
  onRequirePremium?: (featureName: string) => void;
  onViewProfile?: (userId: string) => void;
}

interface Partner {
  id: string;
  name: string;
  category: 'contabilidade' | 'tecnologia' | 'juridico' | 'consorcio' | 'saude' | 'informatica' | 'vestuario';
  categoryLabel: string;
  description: string;
  benefits: string[];
  contactEmail?: string;
  whatsappUrl?: string;
  websiteUrl?: string;
  instagramUrl?: string;
  profileId?: string;
  status: 'active' | 'upcoming';
  logoText: string;
  gradient: string;
  badgeText?: string;
}

export const PartnersHub: React.FC<PartnersHubProps> = ({ 
  userProfile, 
  isPremiumOrVip, 
  onRequirePremium,
  onViewProfile
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const partners: Partner[] = [
    {
      id: 'contabilidade-mei',
      name: 'Marcello M. Bernardo - Contabilidade MEI',
      category: 'contabilidade',
      categoryLabel: 'Contabilidade',
      description: 'Regularização de MEI, declaração anual, emissão de notas fiscais e assessoria tributária completa sob medida para motoristas de aplicativo e profissionais autônomos.',
      benefits: ['Abertura de MEI simplificada', 'Declaração Anual de Faturamento', 'Emissão rápida de Notas Fiscais'],
      contactEmail: 'mmontefuscobernardo@gmail.com',
      whatsappUrl: 'https://wa.me/5511949862676',
      websiteUrl: 'https://consultor-mei-ten.vercel.app/',
      profileId: '974d2776-3a71-41b5-8ab3-92b76b0b0238', // mmontefuscobernardo ID
      status: 'active',
      logoText: 'MB',
      gradient: 'from-emerald-500 to-teal-600',
      badgeText: 'Parceiro Homologado'
    },
    {
      id: 'desenvolvimento-tiago',
      name: 'Romanosv3 studio - Tiago Romão',
      category: 'tecnologia',
      categoryLabel: 'Tecnologia',
      description: 'Desenvolvimento de sistemas corporativos, aplicativos web/mobile sob medida, automações inteligentes de processos, robôs para Telegram/WhatsApp e integrações gerais.',
      benefits: ['Sistemas web sob medida', 'Desenvolvimento de Apps', 'Automações de processos e APIs'],
      contactEmail: 'tiagoromao25@gmail.com',
      whatsappUrl: 'https://wa.me/5511974036983',
      websiteUrl: 'https://romanosv3-studio.web.app', // Firebase hosting URL
      profileId: '7fc3c782-c5b2-4f27-a242-4fe0a23f4fa4', // Tiago ID
      status: 'active',
      logoText: 'R3',
      gradient: 'from-orange-500 to-amber-600',
      badgeText: 'Desenvolvedor Oficial'
    },
    {
      id: 'juridico-upcoming',
      name: 'Consultoria e Proteção Jurídica',
      category: 'juridico',
      categoryLabel: 'Jurídico',
      description: 'Defesa jurídica especializada para motoristas de aplicativos, contratos, termos de uso, assessoria contra bloqueios arbitrários e suporte completo para o trabalhador autônomo.',
      benefits: ['Suporte em casos de bloqueios', 'Consultoria preventiva', 'Revisão de contratos e termos'],
      status: 'upcoming',
      logoText: '⚖️',
      gradient: 'from-purple-500 to-indigo-700',
      badgeText: 'Em Negociação'
    },
    {
      id: 'consorcio-ademicom',
      name: 'Thayná Hokpat - Ademicon Consórcio',
      category: 'consorcio',
      categoryLabel: 'Consórcio',
      description: 'Te ajudo a realizar o seu projeto de vida através do consórcio! Planeje de forma inteligente a compra ou renovação do seu carro, moto ou expansão da sua frota com taxas altamente exclusivas.',
      benefits: ['Consórcios e Investimentos', 'Planejamento de Vida e Frotas', 'Consultoria dedicada para motoristas'],
      contactEmail: 'thaynahokpat@gmail.com',
      whatsappUrl: 'https://wa.me/554191125987',
      instagramUrl: 'https://www.instagram.com/thaynahokpat?igsh=eWNoNWFkYjVpbGN0',
      status: 'active',
      logoText: 'TH',
      gradient: 'from-blue-500 to-indigo-600',
      badgeText: 'Parceira Homologada'
    },
    {
      id: 'saude-upcoming',
      name: 'Convênios de Saúde & Cuidados',
      category: 'saude',
      categoryLabel: 'Saúde',
      description: 'Parcerias com operadoras de planos de saúde, telemedicina e assistência familiar com preços e condições desenhadas para quem trabalha na pista.',
      benefits: ['Telemedicina inclusa', 'Desconto em medicamentos', 'Atendimento nacional'],
      status: 'upcoming',
      logoText: '🩺',
      gradient: 'from-rose-500 to-pink-600',
      badgeText: 'Em Negociação'
    },
    {
      id: 'informatica-upcoming',
      name: 'Informática e Equipamentos',
      category: 'informatica',
      categoryLabel: 'Informatica',
      description: 'Hardware, suporte técnico de TI especializado, acessórios automotivos inteligentes, celulares para trabalho e equipamentos com cupons de descontos exclusivos.',
      benefits: ['Cupons de desconto', 'Garantia estendida para motoristas', 'Suporte rápido'],
      status: 'upcoming',
      logoText: '💻',
      gradient: 'from-slate-500 to-slate-700',
      badgeText: 'Em Negociação'
    },
    {
      id: 'vestuario-upcoming',
      name: 'Vestuário e Conveniência',
      category: 'vestuario',
      categoryLabel: 'Vestuário',
      description: 'Roupas profissionais confortáveis para longas jornadas de trabalho, calçados ergonômicos e acessórios de conveniência que melhoram o seu dia a dia profissional.',
      benefits: ['Desconto em vestuário profissional', 'Acessórios ergonômicos', 'Kits personalizados'],
      status: 'upcoming',
      logoText: '👔',
      gradient: 'from-cyan-500 to-blue-600',
      badgeText: 'Em Negociação'
    }
  ];

  const getIconForCategory = (category: string) => {
    switch (category) {
      case 'contabilidade': return <Briefcase className="w-5 h-5 text-emerald-400" />;
      case 'tecnologia': return <Laptop className="w-5 h-5 text-orange-400" />;
      case 'juridico': return <Shield className="w-5 h-5 text-purple-400" />;
      case 'consorcio': return <Landmark className="w-5 h-5 text-blue-400" />;
      case 'saude': return <HeartPulse className="w-5 h-5 text-rose-400" />;
      case 'informatica': return <Laptop className="w-5 h-5 text-slate-400" />;
      case 'vestuario': return <Shirt className="w-5 h-5 text-cyan-400" />;
      default: return <Briefcase className="w-5 h-5 text-slate-400" />;
    }
  };

  // Filter partners
  const filteredPartners = partners.filter(p => {
    const matchesSearch = p.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          p.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          p.benefits.some(b => b.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = categoryFilter === 'all' || p.category === categoryFilter;
    const matchesStatus = statusFilter === 'all' || p.status === statusFilter;

    return matchesSearch && matchesCategory && matchesStatus;
  });

  return (
    <div className="flex-1 flex flex-col space-y-6 animate-fade-in pb-12">
      {/* Header section with Premium design */}
      <div className="bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700/80 rounded-3xl p-6 md:p-8 shadow-2xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-orange-500/10 to-orange-700/10 rounded-bl-full blur-3xl pointer-events-none"></div>
        <div className="absolute -left-16 -bottom-16 w-48 h-48 bg-blue-500/10 rounded-full blur-3xl pointer-events-none"></div>

        <div className="relative z-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
          <div className="space-y-3">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-500/10 border border-orange-500/30 text-orange-400 text-xs font-bold uppercase tracking-wider">
              <Crown className="w-3.5 h-3.5 fill-current" /> Clube de Benefícios & Parcerias
            </div>
            <h2 className="text-3xl font-black text-white tracking-tight">Parceiros Homologados</h2>
            <p className="text-slate-400 text-sm leading-relaxed max-w-xl">
              Tenha acesso direto a soluções estratégicas para o seu dia a dia profissional. Condições e assessoria exclusivas para os membros da nossa comunidade.
            </p>
          </div>

          <div className="bg-slate-900/60 border border-slate-800 p-4 rounded-2xl flex items-start gap-3 max-w-xs">
            <Info className="w-5 h-5 text-orange-500 shrink-0 mt-0.5" />
            <div className="text-xs text-slate-400 leading-normal">
              <span className="font-bold text-white block mb-0.5">⚠️ Importante</span>
              Até o dia <span className="text-orange-400 font-bold">11 de Junho</span>, todos os contatos estão liberados. Após essa data, o clube será exclusivo para membros **Premium/VIP**.
            </div>
          </div>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="flex flex-col md:flex-row gap-4 items-center justify-between bg-slate-900/40 border border-slate-800 p-4 rounded-2xl">
        {/* Search */}
        <div className="relative w-full md:w-80">
          <Search className="absolute left-3.5 top-3.5 text-slate-500 w-4 h-4" />
          <input
            type="text"
            placeholder="Pesquisar parceiros ou benefícios..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-slate-900 border border-slate-700/60 rounded-xl pl-10 pr-4 py-3 text-white placeholder-slate-500 text-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none transition-all"
          />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 w-full md:w-auto justify-end">
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="bg-slate-900 border border-slate-700/60 rounded-xl px-3 py-2.5 text-slate-300 text-xs font-semibold focus:border-orange-500 outline-none"
          >
            <option value="all">Todas as Categorias</option>
            <option value="contabilidade">Contabilidade</option>
            <option value="tecnologia">Tecnologia</option>
            <option value="juridico">Jurídico</option>
            <option value="consorcio">Consórcio</option>
            <option value="saude">Saúde</option>
            <option value="informatica">Informática</option>
            <option value="vestuario">Vestuário</option>
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-slate-900 border border-slate-700/60 rounded-xl px-3 py-2.5 text-slate-300 text-xs font-semibold focus:border-orange-500 outline-none"
          >
            <option value="all">Todos os Status</option>
            <option value="active">Ativos</option>
            <option value="upcoming">Em Breve</option>
          </select>
        </div>
      </div>

      {/* Partners Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {filteredPartners.length > 0 ? (
          filteredPartners.map(partner => (
            <div 
              key={partner.id}
              className={`flex flex-col bg-slate-900/60 border rounded-3xl p-6 shadow-xl transition-all duration-300 relative group overflow-hidden ${
                partner.status === 'active' 
                  ? 'border-slate-800 hover:border-slate-700 hover:bg-slate-900/80 hover:translate-y-[-2px]' 
                  : 'border-slate-800/40 opacity-75'
              }`}
            >
              {/* Partner Gradient Accent Line */}
              <div className={`absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r ${partner.gradient}`}></div>

              {/* Top Row: Icon/Logo & Badges */}
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${partner.gradient} flex items-center justify-center font-bold text-white text-base shadow-lg shadow-black/30`}>
                    {partner.logoText}
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-white tracking-tight leading-tight group-hover:text-orange-400 transition-colors">
                      {partner.name}
                    </h3>
                    <div className="flex items-center gap-1.5 mt-1.5">
                      {getIconForCategory(partner.category)}
                      <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">{partner.categoryLabel}</span>
                    </div>
                  </div>
                </div>

                <span className={`text-[10px] font-black px-2.5 py-1 rounded-full uppercase tracking-wider border ${
                  partner.status === 'active'
                    ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                    : 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20'
                }`}>
                  {partner.status === 'active' ? 'Ativo' : 'Em Breve'}
                </span>
              </div>

              {/* Description */}
              <p className="text-slate-400 text-xs leading-relaxed mb-5 flex-1">
                {partner.description}
              </p>

              {/* Benefits checklist */}
              <div className="space-y-2 mb-6">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Principais Soluções / Benefícios</p>
                <div className="grid grid-cols-1 gap-1.5">
                  {partner.benefits.map((benefit, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-slate-300">
                      <div className={`w-1.5 h-1.5 rounded-full bg-gradient-to-r ${partner.gradient}`}></div>
                      <span>{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              {partner.status === 'active' ? (
                <div className="flex flex-col gap-2 pt-2">
                  <div className="flex gap-2">
                    {/* WhatsApp Action */}
                    {partner.whatsappUrl && (
                      <a
                        href={partner.whatsappUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 active:scale-95 text-white font-extrabold text-xs py-3 px-4 rounded-xl flex items-center justify-center gap-1.5 shadow-md shadow-emerald-900/10 transition-all"
                      >
                        <MessageSquare className="w-4 h-4 fill-current" />
                        <span>WhatsApp</span>
                      </a>
                    )}
                    
                    {/* Instagram Action */}
                    {partner.instagramUrl && (
                      <a
                        href={partner.instagramUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 bg-gradient-to-r from-pink-600 to-rose-500 hover:from-pink-500 hover:to-rose-400 active:scale-95 text-white font-extrabold text-xs py-3 px-4 rounded-xl flex items-center justify-center gap-1.5 shadow-md shadow-rose-900/10 transition-all"
                      >
                        <Instagram className="w-4 h-4" />
                        <span>Instagram</span>
                      </a>
                    )}
                    
                    {/* Website Action */}
                    {partner.websiteUrl && (
                      <a
                        href={partner.websiteUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 bg-slate-800 hover:bg-slate-700/80 active:scale-95 text-slate-200 border border-slate-700/80 font-bold text-xs py-3 px-4 rounded-xl flex items-center justify-center gap-1.5 transition-all"
                      >
                        <Globe className="w-4 h-4 text-orange-500" />
                        <span>Acessar Site</span>
                      </a>
                    )}
                  </div>

                  <div className="flex gap-2">
                    {/* View Profile inside Driver Score */}
                    {partner.profileId && onViewProfile && (
                      <button
                        onClick={() => onViewProfile(partner.profileId!)}
                        className="w-full bg-slate-900 hover:bg-slate-800 active:scale-95 border border-slate-800 text-slate-400 hover:text-white font-bold text-xs py-2.5 px-4 rounded-xl flex items-center justify-center gap-1.5 transition-all"
                      >
                        <Users className="w-3.5 h-3.5 text-orange-500" />
                        <span>Ver Perfil no App</span>
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="w-full bg-slate-950/40 border border-slate-900 text-slate-500 font-bold text-xs py-3 px-4 rounded-xl flex items-center justify-center gap-2 select-none">
                  <Landmark className="w-4 h-4" />
                  <span>{partner.badgeText || 'Em Fase de Homologação'}</span>
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="col-span-2 text-center py-12 bg-slate-900/30 border border-slate-800 rounded-3xl space-y-3">
            <span className="text-3xl block">🔍</span>
            <h4 className="text-white font-bold text-sm">Nenhum parceiro encontrado</h4>
            <p className="text-slate-500 text-xs max-w-xs mx-auto">Tente ajustar seus termos de pesquisa ou filtros de categoria.</p>
          </div>
        )}
      </div>

      {/* Banner "Divulgue seu negócio" */}
      <div className="bg-gradient-to-r from-blue-900/30 via-slate-900/40 to-indigo-900/30 border border-blue-500/20 rounded-3xl p-6 flex flex-col md:flex-row items-center justify-between gap-6 relative overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl pointer-events-none"></div>
        <div className="flex items-center gap-4 relative z-10">
          <div className="w-12 h-12 rounded-2xl bg-blue-600/20 border border-blue-500/30 flex items-center justify-center shrink-0">
            <Users className="w-6 h-6 text-blue-400" />
          </div>
          <div className="space-y-1">
            <h4 className="text-white font-bold text-sm">💡 Quer ser um parceiro homologado?</h4>
            <p className="text-slate-400 text-xs leading-normal max-w-md">
              Você também corre na pista e toca um negócio próprio em paralelo? Quer oferecer seu serviço ou produto aqui para nossa base de usuários? Venha se juntar ao nosso clube!
            </p>
          </div>
        </div>

        <a
          href={`mailto:tiagoromao25@gmail.com?subject=${encodeURIComponent('Interesse em Divulgação na Aba de Parceiros')}&body=${encodeURIComponent('Olá Tiago,\n\nGostaria de divulgar meu negócio na aba de parceiros homologados do Driver Score.\n\nFico no aguardo.')}`}
          className="shrink-0 bg-blue-600 hover:bg-blue-500 active:scale-95 text-white font-bold text-xs py-3 px-6 rounded-xl shadow-lg shadow-blue-900/20 transition-all flex items-center gap-2"
        >
          <MessageSquare className="w-4 h-4" />
          <span>Falar com o Tiago no Privado</span>
        </a>
      </div>
    </div>
  );
};
